import { useState, type ComponentProps } from 'react'
import Markdown from 'react-markdown'
import { Link, useParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { Button, Card, ErrorPanel, Skeleton } from '../components/ui'
import { useResource } from '../hooks/useResource'
import { useApi } from '../lib/api-context'
import { navigateTo } from '../lib/browser'
import { ApiRequestError } from '../lib/api'
import { describeError } from '../lib/errors'
import { formatDateTime } from '../lib/format'

const RESULTS = new Set(['pass', 'partial', 'miss'])

/** The one cell worth reading at a glance: a section's verdict, coloured and still spelled out. */
function Cell({ children, node: _node, ...rest }: ComponentProps<'td'> & { node?: unknown }) {
  const text = typeof children === 'string' ? children.trim() : ''
  if (!RESULTS.has(text)) return <td {...rest}>{children}</td>

  return (
    <td {...rest}>
      <span className={`badge badge--${text}`}>
        <span className="badge__dot" aria-hidden="true" />
        {text}
      </span>
    </td>
  )
}

function DownloadButton({ artifactId }: { artifactId: string }) {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      const { url } = await api.downloadArtifact(artifactId)
      navigateTo(url)
    } catch (cause: unknown) {
      setError(cause)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button variant="secondary" onClick={() => void download()} disabled={busy}>
        {busy ? 'Preparing…' : 'Download the file'}
      </Button>
      {error ? (
        <div className="field__error" role="alert">
          {describeError(error).title}
        </div>
      ) : null}
    </>
  )
}

/**
 * `DETACHLY_REPORT.md` on screen. The same file ships inside the zip — a customer should not have
 * to unzip a build to find out what the build could not do (F18).
 */
export function ReportPage() {
  const { jobId = '' } = useParams()
  const api = useApi()
  const { data, error, loading, reload } = useResource(() => api.getJobReport(jobId), [jobId])

  if (loading) return <Skeleton rows={5} />

  // A job with no report is a normal answer here — a scan or a scaffold never writes one.
  if (error instanceof ApiRequestError && error.code === 'NOT_FOUND') {
    return (
      <div className="stack">
        <div className="notice" role="status">
          <p className="notice__title">There is no report to show for this job.</p>
          <p>{error.message}</p>
        </div>
        <div>
          <Link to={`/app/jobs/${jobId}`} className="button button--secondary">
            Back to the job
          </Link>
        </div>
      </div>
    )
  }
  if (error) return <ErrorPanel error={error} onRetry={reload} />
  if (!data) return null

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Rebuild report</h1>
          <p className="muted">
            {data.finishedAt ? `Built ${formatDateTime(data.finishedAt)}` : 'Not finished yet'}
          </p>
        </div>
        <Link to={`/app/jobs/${jobId}`} className="button button--secondary">
          Back to the job
        </Link>
        <Link to={`/app/projects/${data.projectId}`} className="button button--secondary">
          Project
        </Link>
      </div>

      <div className="stack">
        <Card>
          <article className="markdown">
            <Markdown remarkPlugins={[remarkGfm]} components={{ td: Cell }}>
              {data.markdown}
            </Markdown>
          </article>
        </Card>

        <Card flat>
          <p className="muted">
            This report also ships as <code className="mono">DETACHLY_REPORT.md</code> at the root
            of the generated project.
          </p>
          <DownloadButton artifactId={data.artifactId} />
        </Card>
      </div>
    </>
  )
}
